import { defineComponent, reactive, SetupContext } from '@vue/composition-api';

// リアクティブな変数の型定義
export type reactiveData = {
    Menu: boolean;
    mainTitle: string;
    menus: {
        text: string;
        icon: string;
        active: boolean;
        isFocus?: boolean;
        link?: string;
        isExistChild: boolean;
        children: {
            text: string;
            icon: string;
            link: string;
            isFocus: boolean;
        }[]
    }[];
    list: boolean;
    isSizeOver: boolean;
};
/**
 * 共通画面コンポーネント
 *
 * ヘッダ/サイドメニュー/各機能(router-outlet)の基盤となる
 */
export default defineComponent({
    setup(props, ctx: SetupContext) {
        const state: reactiveData = reactive<reactiveData>({
            Menu: false,
            mainTitle: '',
            list: false,
            menus: [
                {
                    text: 'ユーザー管理',
                    icon: 'mdi-account',
                    active: false,
                    isExistChild: true,
                    children: [
                        {
                            text: 'ユーザー一覧',
                            icon: 'mdi-format-list-bulleted',
                            link: '/users/list',
                            isFocus: false
                        },
                        {
                            text: 'ユーザー一括登録',
                            icon: 'mdi-file-upload-outline',
                            link: '/bulk-add-users/upload',
                            isFocus: false
                        },
                        {
                            text: '処理状況',
                            icon: 'mdi-list-status',
                            link: '/processing-status-users/list',
                            isFocus: false
                        },
                    ]
                },
                {
                    text: '組織管理',
                    icon: 'mdi-bank',
                    active: false,
                    isExistChild: true,
                    children: [
                        {
                            text: '組織一覧',
                            icon: 'mdi-format-list-bulleted',
                            link: '/organizations/list',
                            isFocus: false
                        },
                        {
                            text: '処理状況',
                            icon: 'mdi-list-status',
                            link: '/processing-status-organizations/list',
                            isFocus: false
                        },
                    ]
                },
                {
                    text: '処理依頼管理',
                    icon: 'mdi-clipboard-outline',
                    active: false,
                    isExistChild: true,
                    children: [
                        {
                            text: '処理依頼一覧',
                            icon: 'mdi-format-list-bulleted',
                            link: '/processing-requests/list',
                            isFocus: false
                        }
                    ]
                },
                {
                    text: 'パラメータ管理',
                    icon: 'mdi-database-cog',
                    active: false,
                    isFocus: false,
                    link: '/parameter-settings',
                    isExistChild: false,
                    children: []
                },
                {
                    text: '年度更新',
                    icon: 'mdi-calendar-arrow-right',
                    active: false,
                    isExistChild: true,
                    children: [
                        {
                            text: '所属コード変換',
                            icon: 'mdi-database-sync',
                            link: '/fiscal-year-update/affiliation-code-conversion',
                            isFocus: false
                        },
                        {
                            text: '人事異動データ変換',
                            icon: 'mdi-file-download-outline',
                            link: '/fiscal-year-update/personnel-change/upload',
                            isFocus: false
                        },
                        {
                            text: '通知データ作成',
                            icon: 'mdi-file-key-outline',
                            link: '/fiscal-year-update/notification-data/upload',
                            isFocus: false
                        }
                    ]
                }
            ],
            isSizeOver: false,          // ログインユーザー名が超過したか
        })

        /**
         * SAML認証状態取得
         */
        function getSamlAuthenticationStatus() {
            return ctx.root.$store.state.isSamlAuthenticationStatus;
        }

        /**
         * メインヘッダー部に表示するログインユーザー名取得
         */
        function getLoginUserName() {
            // ログインユーザー情報がストアに保存されていない場合処理を行わない
            if (ctx.root.$store.state.authenticationUser === null) {
                return;
            }

            // ストアに保存しているログインユーザー名を取得
            let loginUserName = ctx.root.$store.state.authenticationUser.userName;

            // ログインユーザー名が10文字より多い場合、11文字以降を表示しない
            if (loginUserName.length > 10) {
                // ログインユーザー名が超過したかのフラグをtrueにする
                state.isSizeOver = true;

                // 名前の10文字目までを取得し、11文字目を三点リーダーにする
                loginUserName = loginUserName.substring(0, 10).concat('...');
            }

            return loginUserName;
        }

        /**
         * ツールチップで表示するログインユーザーフルネーム取得
         */
        function getLoginUserFullName() {
            // ログインユーザー情報がストアに保存されていない場合処理を行わない
            if (ctx.root.$store.state.authenticationUser === null) {
                return;
            }

            return ctx.root.$store.state.authenticationUser.userName;
        }

        /**
         * タイトルアイコンの取得
         * 
         * @returns string タイトルアイコン
         */
        function getTitleIcon() {
            return ctx.root.$store.state.titleIcon;
        }

        /**
         * メインタイトルの取得
         * 
         * @returns string メインタイトル
         */
        function getMainTitle() {
            return ctx.root.$store.state.mainTitle;
        }

        /**
         * メインメニュー開閉処理
         */
        function menuOpenOrClose() {
            // 展開されているツリーを閉じる
            state.menus.forEach((value, index) => {
                if (state.menus[index].active === true) {
                    state.menus[index].active = !state.menus[index].active;
                }
            })

            // メニューを開閉する
            state.Menu = !state.Menu;
        }

        /**
         * メインメニューの選択状態切替
         * 
         * @param link 選択されたリンクの情報
         */
        function changeFocus(link: string) {
            // 親の項目を特定する
            for (let i = 0; i < state.menus.length; i++) {
                if (!state.menus[i].isExistChild) {
                    // 親にリンクが存在する場合（子が存在しない）
                    // 引数の項目と一致するか
                    if (state.menus[i].link === link) {
                        // 一致する項目の選択状態をtrueにする
                        state.menus[i].isFocus = true;
                    } else {
                        // 他の項目の選択状態をfalseにする
                        state.menus[i].isFocus = false;
                    }
                } else {
                    // 子が存在する場合
                    for (let j = 0; j < state.menus[i].children.length; j++) {
                        // 引数の項目と一致するか
                        if (state.menus[i].children[j].link === link) {
                            // 一致する項目の選択状態をtrueにする
                            state.menus[i].children[j].isFocus = true;
                        } else {
                            // 他の項目の選択状態をfalseにする
                            state.menus[i].children[j].isFocus = false;
                        }
                    }
                }
            }
        }

        /**
         * アイコンカラー変更処理
         * 
         * @param child 親の持つ子の情報
         * @return boolean アクティブ状態フラグにtrueのものがあればtrue、無ければfalse
         */
        function iconColor(child: any) {
            // カウンター初期化
            let count = 0;

            // 子の数を特定する
            for (let i = 0; i < child.length; i++) {
                // アクティブ状態フラグがtrueのものがある場合、カウンターをインクリメントする
                if (child[i].isFocus) {
                    count++;
                }
            }

            // カウンターが0より大きい場合、trueを返す
            // それ以外はfalseを返す
            if (count > 0) {
                return true;
            } else {
                return false;
            }
        }

        // ウインドウ上でマウス移動が行われたらcheckURLメソッドを呼ぶ
        window.addEventListener("mousemove", checkURL);

        /**
         * マウス移動イベントのアクティブ状態変更処理
         */
        function checkURL() {
            // マウス移動イベントが行われたとき存在するURLにいる場合、アクティブ状態を変更する
            //     現在いるページのパスに特定の文字列が含まれる場合、
            //     メソッドの引数にアクティブ状態にするリンクを設定している
            switch (true) {
                case /\/users\//.test(location.pathname):
                    changeFocus('/users/list');
                    break;

                case /\/bulk-add-users\//.test(location.pathname):
                    changeFocus('/bulk-add-users/upload');
                    break;

                case /\/processing-status-users\//.test(location.pathname):
                    changeFocus('/processing-status-users/list');
                    break;

                case /\/organizations\//.test(location.pathname):
                    changeFocus('/organizations/list');
                    break;

                case /\/processing-status-organizations\//.test(location.pathname):
                    changeFocus('/processing-status-organizations/list');
                    break;

                case /\/processing-requests\//.test(location.pathname):
                    changeFocus('/processing-requests/list');
                    break;

                case /\/parameter-settings/.test(location.pathname):
                    changeFocus('/parameter-settings');
                    break;

                case /\/fiscal-year-update\/affiliation-code-conversion/.test(location.pathname):
                    changeFocus('/fiscal-year-update/affiliation-code-conversion');
                    break;

                case /\/fiscal-year-update\/personnel-change/.test(location.pathname):
                    changeFocus('/fiscal-year-update/personnel-change/upload');
                    break;

                case /\/fiscal-year-update\/notification-data/.test(location.pathname):
                    changeFocus('/fiscal-year-update/notification-data/upload');
                    break;
            }
        }

        return {
            state,
            menuOpenOrClose,
            getTitleIcon,
            getMainTitle,
            getSamlAuthenticationStatus,
            getLoginUserName,
            getLoginUserFullName,
            changeFocus,
            iconColor
        };
    }
});
